import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { appApi } from './api';

export type ChatRole = 'user' | 'assistant';

export interface ChatMessage {
    id: string;
    role: ChatRole;
    content: string;
    timestamp: number;
}

export interface SelectedFeature {
    id: string;
    type: string;
    chromosome: string;
    start: number;
    end: number;
    strand?: '+' | '-' | '.' | '?';
    attributes: Record<string, string>;
    /** Original column-9 string from the GFF, sent verbatim as backend's selected_element.raw */
    attributeString?: string;
    parent?: SelectedFeature;
}

export interface VisibleRegion {
    chromosome: string;
    start: number;
    end: number;
}

interface ChatState {
    messages: ChatMessage[];
    selectedFeature: SelectedFeature | null;
    isSelectedPanelOpen: boolean;
    visibleRegion: VisibleRegion | null;
    isStreaming: boolean;
    requestId: string | null;
    gffUrl: string | null;
}

const initialState: ChatState = {
    messages: [],
    selectedFeature: null,
    isSelectedPanelOpen: false,
    visibleRegion: null,
    isStreaming: false,
    requestId: null,
    gffUrl: null,
};

export const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        addMessage(state, action: PayloadAction<ChatMessage>) {
            state.messages.push(action.payload);
        },
        appendToLastAssistant(state, action: PayloadAction<string>) {
            const last = state.messages[state.messages.length - 1];
            if (last && last.role === 'assistant') {
                last.content += action.payload;
            }
        },
        clearChat(state) {
            state.messages = [];
            state.isStreaming = false;
        },
        setSelectedFeature(state, action: PayloadAction<SelectedFeature | null>) {
            state.selectedFeature = action.payload;
            state.isSelectedPanelOpen = action.payload !== null;
        },
        closeSelectedPanel(state) {
            state.isSelectedPanelOpen = false;
        },
        setVisibleRegion(state, action: PayloadAction<VisibleRegion | null>) {
            state.visibleRegion = action.payload;
        },
        setStreaming(state, action: PayloadAction<boolean>) {
            state.isStreaming = action.payload;
        },
        setRequestId(state, action: PayloadAction<string | null>) {
            state.requestId = action.payload;
        },
        setGffUrl(state, action: PayloadAction<string | null>) {
            state.gffUrl = action.payload;
        },
        resetChat() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(appApi.endpoints.upload.matchPending, () => initialState);
    },
});

export const {
    addMessage,
    appendToLastAssistant,
    clearChat,
    setSelectedFeature,
    closeSelectedPanel,
    setVisibleRegion,
    setStreaming,
    setRequestId,
    setGffUrl,
    resetChat,
} = chatSlice.actions;

export default chatSlice.reducer;
